import type {
  CacheConfig,
  CacheEntry,
  CacheStats,
  CacheStrategy,
} from "./types";

/**
 * In-memory cache manager with TTL, event-driven invalidation,
 * and pluggable fetch strategies.
 *
 * Usage:
 *   const cache = new CacheManager();
 *   cache.configure("currentUser", { ttl: 300, strategy: "cache-first", invalidateOn: ["AUTH_LOGOUT_SUCCESS"], storage: "memory" });
 *   const user = await cache.get("currentUser", () => fetchCurrentUser());
 */
export class CacheManager {
  private store = new Map<string, CacheEntry>();
  private configs = new Map<string, CacheConfig>();
  private stats: CacheStats = { hits: 0, misses: 0, evictions: 0, size: 0 };
  private eventSubscriptions: Array<() => void> = [];

  /** Register a cache key with its configuration. */
  configure(key: string, config: CacheConfig): void {
    this.configs.set(key, config);
  }

  /** Bulk-configure from a domain registry's cache.strategies map. */
  configureFromStrategies(strategies: Record<string, CacheConfig>): void {
    for (const [key, config] of Object.entries(strategies)) {
      this.configure(key, config);
    }
  }

  /**
   * Get data from cache or fetch it using the provided loader.
   * Respects the configured strategy for this key.
   */
  async get<T>(key: string, loader: () => Promise<T>): Promise<T> {
    const config = this.configs.get(key);
    const strategy: CacheStrategy = config?.strategy ?? "network-first";

    switch (strategy) {
      case "cache-first":
        return this.cacheFirst<T>(key, loader);
      case "network-first":
        return this.networkFirst<T>(key, loader);
      case "stale-while-revalidate":
        return this.staleWhileRevalidate<T>(key, loader);
      default:
        return this.networkFirst<T>(key, loader);
    }
  }

  /** Return cached data if fresh, else fetch. */
  private async cacheFirst<T>(
    key: string,
    loader: () => Promise<T>,
  ): Promise<T> {
    const entry = this.getEntry<T>(key);
    if (entry && !this.isExpired(entry)) {
      this.stats.hits++;
      return entry.data;
    }

    this.stats.misses++;
    return this.fetchAndStore<T>(key, loader);
  }

  /** Always fetch from network, fall back to cache on error. */
  private async networkFirst<T>(
    key: string,
    loader: () => Promise<T>,
  ): Promise<T> {
    try {
      this.stats.misses++;
      return await this.fetchAndStore<T>(key, loader);
    } catch (error) {
      const entry = this.getEntry<T>(key);
      if (entry) {
        this.stats.hits++;
        return entry.data;
      }
      throw error;
    }
  }

  /** Return stale data immediately, revalidate in background. */
  private async staleWhileRevalidate<T>(
    key: string,
    loader: () => Promise<T>,
  ): Promise<T> {
    const entry = this.getEntry<T>(key);

    if (entry) {
      this.stats.hits++;
      // Revalidate in background if stale
      if (this.isExpired(entry)) {
        this.fetchAndStore<T>(key, loader).catch(() => {
          // Silent fail on background revalidation
        });
      }
      return entry.data;
    }

    this.stats.misses++;
    return this.fetchAndStore<T>(key, loader);
  }

  /** Fetch data and store in cache. */
  private async fetchAndStore<T>(
    key: string,
    loader: () => Promise<T>,
  ): Promise<T> {
    const data = await loader();
    this.set(key, data);
    return data;
  }

  /** Manually set a cache entry. */
  set<T>(key: string, data: T): void {
    const config = this.configs.get(key);
    const ttl = config?.ttl ?? 300;

    this.store.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl * 1000, // convert to ms
      key,
    });
    this.stats.size = this.store.size;
  }

  /** Check if a cache entry exists and is fresh. */
  has(key: string): boolean {
    const entry = this.getEntry(key);
    return entry !== null && !this.isExpired(entry);
  }

  /** Invalidate a specific cache key. */
  invalidate(key: string): void {
    if (this.store.delete(key)) {
      this.stats.evictions++;
      this.stats.size = this.store.size;
    }
  }

  /** Invalidate all cache keys that listen to the given event. */
  invalidateByEvent(eventName: string): void {
    for (const [key, config] of this.configs.entries()) {
      if (config.invalidateOn.includes(eventName)) {
        this.invalidate(key);
      }
    }
  }

  /** Clear the entire cache. */
  clear(): void {
    this.stats.evictions += this.store.size;
    this.store.clear();
    this.stats.size = 0;
  }

  /** Get cache statistics. */
  getStats(): Readonly<CacheStats> {
    return { ...this.stats };
  }

  /**
   * Subscribe to an EventBus for automatic invalidation.
   * Listens to all events declared in configured cache strategies.
   */
  subscribeToEvents(eventBus: {
    subscribe: (event: string, handler: (payload: unknown) => void) => string;
    unsubscribe?: (id: string) => void;
  }): void {
    const allEvents = new Set<string>();
    for (const config of this.configs.values()) {
      for (const event of config.invalidateOn) {
        allEvents.add(event);
      }
    }

    for (const event of allEvents) {
      const id = eventBus.subscribe(event, () => {
        this.invalidateByEvent(event);
      });
      if (eventBus.unsubscribe) {
        this.eventSubscriptions.push(() => eventBus.unsubscribe!(id));
      }
    }
  }

  /** Unsubscribe from all events. */
  destroy(): void {
    for (const unsub of this.eventSubscriptions) {
      unsub();
    }
    this.eventSubscriptions = [];
    this.clear();
    this.configs.clear();
  }

  private getEntry<T>(key: string): CacheEntry<T> | null {
    const entry = this.store.get(key);
    return (entry as CacheEntry<T>) ?? null;
  }

  private isExpired(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp > entry.ttl;
  }
}

/** Singleton cache manager instance. */
export const cacheManager = new CacheManager();
